// Phase 2 — Event participation Slots. One component, two modes:
//  • Admin (can_manage): configure slots (enable, allow-multi, time windows
//    + capacity) and manage sign-ups (confirmed + waitlist "Pending Approval").
//  • Agent: see slots with availability and Join / Join Waitlist / Cancel.

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Clock, Plus, Trash2, Check, X, UserCheck, Loader2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DateTimeField from '@/components/common/DateTimeField';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { calendarAPI } from '@/lib/api';
import { localToUTCISO, utcISOToLocal } from './eventFormHelpers';
import { getErrorMessage } from '@/lib/utils';

export default function EventSlotsPanel({ eventId, timezone = 'America/Los_Angeles', onConfigChanged }) {
  const tz = timezone || 'America/Los_Angeles';
  const [data, setData] = useState(null);
  const [signups, setSignups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null); // admin config draft

  const load = useCallback(async () => {
    try {
      const r = await calendarAPI.listSlots(eventId);
      setData(r.data);
      if (r.data?.can_manage) {
        setDraft({
          slots_enabled: !!r.data.slots_enabled,
          slots_allow_multi: !!r.data.slots_allow_multi,
          slots: (r.data.slots || []).map((s) => ({
            slot_id: s.slot_id,
            label: s.label || '',
            start_local: utcISOToLocal(s.start_datetime, tz),
            end_local: utcISOToLocal(s.end_datetime, tz),
            capacity: s.capacity,
          })),
        });
        try {
          const sr = await calendarAPI.listSlotSignups(eventId);
          setSignups(sr.data || []);
        } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load slots'));
    }
  }, [eventId, tz]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;

  const canManage = data.can_manage;

  // ── Admin config ──────────────────────────────────────────────
  const addSlot = () => setDraft((d) => ({ ...d, slots: [...d.slots, { label: '', start_local: '', end_local: '', capacity: 5 }] }));
  const updSlot = (i, k, v) => setDraft((d) => ({ ...d, slots: d.slots.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const delSlot = (i) => setDraft((d) => ({ ...d, slots: d.slots.filter((_, idx) => idx !== i) }));

  const saveConfig = async () => {
    for (const s of draft.slots) {
      if (!s.start_local || !s.end_local) { toast.error('Each slot needs a start and end time'); return; }
      if (s.end_local < s.start_local) { toast.error('Slot end must be after start'); return; }
      if (!s.capacity || Number(s.capacity) < 1) { toast.error('Each slot needs a capacity of at least 1'); return; }
    }
    setBusy(true);
    try {
      const payload = {
        slots_enabled: draft.slots_enabled,
        slots_allow_multi: draft.slots_allow_multi,
        slots: draft.slots.map((s) => ({
          ...(s.slot_id ? { slot_id: s.slot_id } : {}),
          label: s.label?.trim() || null,
          start_datetime: localToUTCISO(s.start_local, tz),
          end_datetime: localToUTCISO(s.end_local, tz),
          capacity: Number(s.capacity),
        })),
      };
      await calendarAPI.updateEvent(eventId, payload);
      toast.success('Slots saved');
      onConfigChanged?.();
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Save failed'));
    } finally { setBusy(false); }
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await load(); }
    catch (e) { toast.error(getErrorMessage(e, 'Action failed')); }
    finally { setBusy(false); }
  };

  if (canManage) {
    const bySlot = (slotId, status) => signups.filter((s) => s.slot_id === slotId && s.status === status);
    return (
      <div className="space-y-6" data-testid="event-slots-admin">
        <div className="rounded-lg border border-slate-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-800">Enable participation slots</div>
              <div className="text-xs text-slate-500">Agents sign up for a specific time window. Best for booths/fairs.</div>
            </div>
            <Switch checked={draft.slots_enabled} onCheckedChange={(v) => setDraft((d) => ({ ...d, slots_enabled: v }))} data-testid="slots-enabled-toggle" />
          </div>
          {draft.slots_enabled && (
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Switch checked={draft.slots_allow_multi} onCheckedChange={(v) => setDraft((d) => ({ ...d, slots_allow_multi: v }))} data-testid="slots-multi-toggle" />
              Allow an agent to select more than one slot
            </label>
          )}
        </div>

        {draft.slots_enabled && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-800">Time slots</h4>
              <Button size="sm" variant="outline" onClick={addSlot} data-testid="slots-add-btn"><Plus size={14} className="mr-1" /> Add slot</Button>
            </div>
            {draft.slots.length === 0 && <p className="text-xs text-slate-500">No slots yet — add your first time window.</p>}
            {draft.slots.map((s, i) => {
              const confirmed = bySlot(s.slot_id, 'confirmed');
              const pending = bySlot(s.slot_id, 'waitlist_pending');
              return (
                <div key={s.slot_id || i} className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid={`slot-row-${i}`}>
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-3"><Label className="text-[11px] text-slate-500">Label</Label><Input value={s.label} onChange={(e) => updSlot(i, 'label', e.target.value)} placeholder="Morning" data-testid={`slot-label-${i}`} /></div>
                    <div className="col-span-3"><Label className="text-[11px] text-slate-500">Start</Label><DateTimeField value={s.start_local} onChange={(v) => updSlot(i, 'start_local', v)} testId={`slot-start-${i}`} /></div>
                    <div className="col-span-3"><Label className="text-[11px] text-slate-500">End</Label><DateTimeField value={s.end_local} onChange={(v) => updSlot(i, 'end_local', v)} testId={`slot-end-${i}`} /></div>
                    <div className="col-span-2"><Label className="text-[11px] text-slate-500">Capacity</Label><Input type="number" min={1} value={s.capacity} onChange={(e) => updSlot(i, 'capacity', e.target.value)} data-testid={`slot-capacity-${i}`} /></div>
                    <div className="col-span-1 flex justify-end"><Button size="icon" variant="ghost" onClick={() => delSlot(i)} data-testid={`slot-del-${i}`}><Trash2 size={15} className="text-red-500" /></Button></div>
                  </div>
                  {s.slot_id && (
                    <div className="flex flex-wrap gap-4 text-xs">
                      <span className="text-emerald-700 font-medium"><Users size={12} className="inline mr-1" />{confirmed.length}/{s.capacity} confirmed</span>
                      {confirmed.map((c) => (
                        <span key={c.signup_id} className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                          {c.name}
                          <button onClick={() => act(() => calendarAPI.removeSlotSignup(eventId, c.signup_id), 'Removed')} data-testid={`slot-remove-${c.signup_id}`}><X size={11} className="text-slate-500 hover:text-red-500" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  {s.slot_id && pending.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-[11px] font-semibold text-amber-700">Waitlist — pending approval</div>
                      {pending.map((p) => (
                        <div key={p.signup_id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded px-2 py-1 text-xs" data-testid={`slot-pending-${p.signup_id}`}>
                          <span>{p.name}{p.agent_npn ? ` · NPN ${p.agent_npn}` : ''}</span>
                          <span className="flex gap-1">
                            <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => act(() => calendarAPI.approveSlotSignup(eventId, p.signup_id), 'Approved')} data-testid={`slot-approve-${p.signup_id}`}><Check size={12} className="mr-1 text-emerald-600" />Approve</Button>
                            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => act(() => calendarAPI.rejectSlotSignup(eventId, p.signup_id), 'Rejected')} data-testid={`slot-reject-${p.signup_id}`}><X size={12} className="mr-1 text-red-500" />Reject</Button>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <Button onClick={saveConfig} disabled={busy} data-testid="slots-save-btn">{busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : null} Save slots</Button>
          </div>
        )}
      </div>
    );
  }

  // ── Agent view ────────────────────────────────────────────────
  if (!data.slots_enabled) {
    return <p className="text-sm text-slate-500" data-testid="event-slots-agent-empty">This event doesn’t use time slots.</p>;
  }
  const fmt = (iso) => (iso ? utcISOToLocal(iso, tz).replace('T', ' ') : '');
  return (
    <div className="space-y-3" data-testid="event-slots-agent">
      <p className="text-xs text-slate-500">Pick {data.slots_allow_multi ? 'one or more slots' : 'a slot'} to participate.</p>
      {data.slots.map((s) => (
        <div key={s.slot_id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3" data-testid={`agent-slot-${s.slot_id}`}>
          <div>
            <div className="text-sm font-medium text-slate-800">{s.label || 'Slot'} <span className="text-slate-400 font-normal">· {fmt(s.start_datetime)}–{fmt(s.end_datetime).slice(-5)}</span></div>
            <div className="text-xs text-slate-500">
              {s.is_full ? <span className="text-red-600 font-medium">Full</span> : <span>{s.remaining} of {s.capacity} spots left</span>}
              {s.waitlist_count > 0 && <span className="ml-2 text-amber-600">{s.waitlist_count} on waitlist</span>}
            </div>
          </div>
          <div>
            {s.my_status === 'confirmed' && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mr-2">Confirmed</Badge>}
            {s.my_status === 'waitlist_pending' && <Badge className="bg-amber-100 text-amber-700 border-amber-200 mr-2">Pending approval</Badge>}
            {s.my_status ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => calendarAPI.leaveSlot(eventId, s.slot_id), 'Cancelled')} data-testid={`agent-slot-cancel-${s.slot_id}`}>Cancel</Button>
            ) : s.is_full ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => calendarAPI.joinSlot(eventId, s.slot_id), 'Added to waitlist — pending approval')} data-testid={`agent-slot-waitlist-${s.slot_id}`}>Join waitlist</Button>
            ) : (
              <Button size="sm" disabled={busy} onClick={() => act(() => calendarAPI.joinSlot(eventId, s.slot_id), 'Slot confirmed')} data-testid={`agent-slot-join-${s.slot_id}`}><UserCheck size={13} className="mr-1" /> I’m participating</Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
